import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';

interface StatusResponse {
  connected: boolean;
  emailAddress: string | null;
  scopes: string[];
  connectedAt: string | null;
  oauthClientConfigured: boolean;
  requiredScopes: string[];
  teamsChatEnabled: boolean;
  missingChatScopes: string[];
  callbackUrl: string;
}

interface GoogleStatus {
  emailAddress: string | null;
}

export function AdminMicrosoftPage(): React.ReactElement {
  const { agent } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const connectedParam = searchParams.get('connected');
    if (connectedParam === '1') {
      setInfo('Microsoft account connected.');
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      next.delete('msg');
      setSearchParams(next, { replace: true });
    } else if (connectedParam === '0') {
      const msg = searchParams.get('msg') ?? 'unknown_error';
      setError(`Connection failed: ${msg}`);
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      next.delete('msg');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function loadStatus() {
    try {
      const res = await apiFetch<StatusResponse>('/api/admin/microsoft/status');
      setStatus(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load status.');
    }
  }

  async function loadGoogleStatus() {
    try {
      const r = await apiFetch<GoogleStatus>('/api/admin/google/status');
      setGoogleEmail(r.emailAddress ?? null);
    } catch {
      setGoogleEmail(null);
    }
  }

  useEffect(() => {
    void loadStatus();
    void loadGoogleStatus();
  }, []);

  function connect() {
    window.location.href = '/api/admin/microsoft/connect';
  }

  async function disconnect() {
    if (!confirm('Disconnect Microsoft account? Teams meeting URLs will stop being created automatically.')) return;
    setBusy(true);
    try {
      await apiFetch('/api/admin/microsoft/disconnect', { method: 'POST' });
      await loadStatus();
      setInfo('Microsoft account disconnected.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to disconnect.');
    } finally {
      setBusy(false);
    }
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Team Microsoft / Teams</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          The <strong>org-level</strong> Teams account. Used to create Microsoft Teams online-meeting
          URLs for client check-ins and team-calendar events, and to send <strong>direct Teams
          messages</strong> to the assigned agent when a ticket is assigned to them or a client
          replies. Calendar invites still go through Google. Each agent connects their own Teams
          license separately under <strong>My profile → My Teams license</strong>.
        </p>
      </div>

      {info && (
        <Card className="border-green-300 bg-green-50/40">
          <p className="text-sm text-green-900">{info}</p>
        </Card>
      )}
      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {!status ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : !status.oauthClientConfigured ? (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Setup needed</h2>
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            Server env vars not set. Add the following in DigitalOcean App Platform → backend
            component → Environment (encrypted):
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-[var(--color-surface)] p-3 text-xs">
{`MICROSOFT_TENANT_ID=<your tenant GUID>
MICROSOFT_CLIENT_ID=<app registration application (client) ID>
MICROSOFT_CLIENT_SECRET=<client secret value>`}
          </pre>
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            Redirect URI to set in your Entra ID app registration: <code>{status.callbackUrl}</code>
          </p>
        </Card>
      ) : (
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                {status.connected ? 'Connected' : 'Not connected'}
              </h2>
              {status.connected && status.emailAddress && (
                <p className="text-sm text-[var(--color-muted)]">
                  <span className="text-xs uppercase tracking-wide">Teams meeting organizer</span>
                  <span className="block text-base font-semibold text-[var(--color-header)]">
                    {status.emailAddress}
                  </span>
                  Every Teams meeting created by T3 (check-ins, calendar events) is organized by
                  this account.
                </p>
              )}
              {status.connected && status.connectedAt && (
                <p className="mt-2 text-xs text-[var(--color-muted)]">
                  Connected {new Date(status.connectedAt).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {status.connected ? (
                <>
                  <Button onClick={connect} variant="outline">
                    Reconnect
                  </Button>
                  <Button onClick={() => void disconnect()} disabled={busy} variant="outline">
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button onClick={connect}>Connect Microsoft</Button>
              )}
            </div>
          </div>

          {status.connected && !status.teamsChatEnabled && (
            <div className="mt-4 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50/40 p-3 text-xs text-amber-900">
              <strong>Teams notifications need a reconnect.</strong>
              <p className="mt-1">
                This connection is missing the chat permissions required to send direct Teams
                messages ({status.missingChatScopes.map((s) => (
                  <code key={s} className="mx-0.5">{s}</code>
                ))}). Click <strong>Reconnect</strong> and approve the new permissions — until then,
                ticket-assigned and client-reply Teams messages won't send (email and in-app
                notifications are unaffected).
              </p>
            </div>
          )}

          {status.connected && status.teamsChatEnabled && (
            <div className="mt-4 rounded-[var(--radius-button)] border border-emerald-300 bg-emerald-50/40 p-3 text-xs text-emerald-900">
              <strong>Teams notifications enabled.</strong> The assigned agent gets a direct Teams
              message from <code>{status.emailAddress}</code> when a ticket is assigned to them or a
              client replies.
            </div>
          )}

          {status.connected && status.emailAddress && googleEmail && (
            status.emailAddress.toLowerCase() === googleEmail.toLowerCase() ? (
              <div className="mt-4 rounded-[var(--radius-button)] border border-emerald-300 bg-emerald-50/40 p-3 text-xs text-emerald-900">
                <strong>Aligned with the Google ticketing inbox.</strong> Teams meetings are
                organized by <code>{status.emailAddress}</code>, and team-calendar events land on
                that same account's primary Google calendar.
              </div>
            ) : (
              <div className="mt-4 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50/40 p-3 text-xs text-amber-900">
                <strong>Mismatch with the Google ticketing inbox.</strong>
                <p className="mt-1">
                  Microsoft is connected as <code>{status.emailAddress}</code>, but the Google
                  ticketing inbox is <code>{googleEmail}</code>. Team-calendar events land on the
                  Google account's primary calendar, but their Teams meetings are organized by the
                  Microsoft account — so the two won't appear together unless this account is also
                  invited.
                </p>
                <p className="mt-2">
                  To align: <strong>Disconnect</strong> above, then sign out of Microsoft in your
                  browser and click <strong>Connect Microsoft</strong> while signed in as{' '}
                  <code>{googleEmail}</code> in your M365 tenant (must have a Teams license).
                </p>
              </div>
            )
          )}

          {status.connected && status.emailAddress && !googleEmail && (
            <div className="mt-4 rounded-[var(--radius-button)] border border-amber-300 bg-amber-50/40 p-3 text-xs text-amber-900">
              Google integration isn't connected yet — connect it under{' '}
              <a href="/admin/google" className="text-[var(--color-t3-blue)] hover:underline">
                Admin → Google integration
              </a>{' '}
              first so team-calendar events have somewhere to land.
            </div>
          )}

          <div className="mt-4 grid gap-3 border-t border-[var(--color-border)] pt-3 text-xs sm:grid-cols-2">
            <div>
              <div className="font-semibold text-[var(--color-header)]">Required scopes</div>
              <ul className="mt-1 space-y-0.5 text-[var(--color-muted)]">
                {status.requiredScopes.map((s) => (
                  <li key={s} className="font-mono">{s}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-[var(--color-header)]">Granted scopes</div>
              <ul className="mt-1 space-y-0.5 text-[var(--color-muted)]">
                {status.scopes.length === 0 ? (
                  <li className="italic">none</li>
                ) : (
                  status.scopes.map((s) => <li key={s} className="font-mono">{s}</li>)
                )}
              </ul>
            </div>
          </div>

          <p className="mt-3 text-xs text-[var(--color-muted)]">
            Redirect URI registered in Entra ID: <code>{status.callbackUrl}</code>
          </p>
        </Card>
      )}
    </div>
  );
}
